import { Switch } from '@components/Switch/Switch';
import { CREDITS_API_TO_DISPLAY_SCALE, pluralize } from '@lib/utils/units';
import { ActionButton } from '@pages/ActionButton';
import { billingStore, SECONDS_IN_HOUR } from '@stores/Billing';
import { debugStore } from '@stores/Debug';
import { useModalStore } from '@stores/ModalStore';
import { paywallStore } from '@stores/Paywall';
import { TrackedEvent, trackingStore } from '@stores/Tracking';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import css from './DebugTab.module.scss';

const EventLogEntry = ({ event }: { event: TrackedEvent }) => {
  const time = new Date(event.timestamp).toLocaleTimeString();
  const props = event.properties ? JSON.stringify(event.properties) : '';
  return (
    <div className={css.eventLogEntry}>
      <span className={css.eventTime}>{time}</span>
      <span className={css.eventName}>{event.event}</span>
      {props && <span className={css.eventProps}>{props}</span>}
    </div>
  );
};

const EventLogPanel = observer(() => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const events = trackingStore.eventLog;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length]);

  return (
    <div className={css.eventLogContainer}>
      <div className={css.eventLogHeader}>
        <span className={css.debugLabel}>Event Log ({pluralize(events.length, 'event')})</span>
        <ActionButton
          variant="secondary"
          onClick={() => trackingStore.clearEventLog()}
          disabled={events.length === 0}
        >
          Clear
        </ActionButton>
      </div>
      <div className={css.eventLogScroll} ref={scrollRef}>
        {events.length === 0 ? (
          <div className={css.eventLogEmpty}>
            No events captured yet. Interact with the app to see events here.
          </div>
        ) : (
          events.map((event, i) => <EventLogEntry key={i} event={event} />)
        )}
      </div>
    </div>
  );
});

export const DebugTab = observer(() => {
  const [loading, setLoading] = useState(false);
  const inDebugMode = debugStore.respectfulDebugMode;
  const modalStore = useModalStore();

  const handleExitDebugMode = async () => {
    setLoading(true);
    try {
      debugStore.setRespectfulDebugMode(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={css.debugContainer}>
      <div className={css.debugInnerContainer}>
        <div className={css.debugField}>
          <label className={css.debugLabel}>
            You are {!inDebugMode ? 'NOT ' : ''}in debug mode.
          </label>
          <ActionButton variant="danger" onClick={handleExitDebugMode} disabled={loading}>
            {loading ? 'Exiting...' : 'Exit Debug Mode'}
          </ActionButton>
        </div>
        {debugStore.respectfulDebugMode && (
          <>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Big Search Bar (for demo purposes)</label>
              <Switch
                checked={debugStore.demoModeValue}
                onChange={(checked) => debugStore.setBigSearchBar(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Story Atlas</label>
              <Switch
                checked={debugStore.storyAtlas}
                onChange={(checked) => debugStore.setStoryAtlas(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>
                Publishing entry points (Schedule nav row, Settings tab) — off previews the public
                view
              </label>
              <Switch
                checked={debugStore.publishingEntryPoints}
                onChange={(checked) => debugStore.setPublishingEntryPoints(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Upload status panel (replaces toasts)</label>
              <Switch
                checked={debugStore.uploadStatus}
                onChange={(checked) => debugStore.setUploadStatus(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>
                Fake Workspace podcast clips (count) — “Real” uses the live fetch
              </label>
              <div
                style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}
              >
                {([null, 0, 1, 2, 3, 4, 5, 6] as const).map((n) => (
                  <ActionButton
                    key={String(n)}
                    variant={debugStore.fakePodcastClipCount === n ? 'primary' : 'secondary'}
                    onClick={() => debugStore.setFakePodcastClipCount(n)}
                    disabled={!inDebugMode}
                  >
                    {n === null ? 'Real' : n}
                  </ActionButton>
                ))}
              </div>
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Force JSON on API requests</label>
              <Switch
                checked={debugStore.forceJsonApi}
                onChange={(checked) => debugStore.setForceJsonApi(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Mock HLS.js unsupported (no MSE)</label>
              <Switch
                checked={debugStore.mockHlsUnsupported}
                onChange={(checked) => debugStore.setMockHlsUnsupported(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Small batch size (2 URLs per batch)</label>
              <Switch
                checked={debugStore.smallBatchSizeMode}
                onChange={(checked) => debugStore.setSmallBatchSizeMode(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>
                Simulate processing modal (hide transcript/visual)
              </label>
              <Switch
                checked={debugStore.simulateProcessingModal}
                onChange={(checked) => debugStore.setSimulateProcessingModal(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Enable unified search</label>
              <Switch
                checked={debugStore.enableUnifiedSearch}
                onChange={(checked) => debugStore.setEnableUnifiedSearch(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Mobile UI</label>
              <Switch
                checked={debugStore.mobileUI}
                onChange={(checked) => debugStore.setMobileUI(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Force first time user</label>
              <Switch
                checked={debugStore.forceFirstTimeUser}
                onChange={(checked) => debugStore.setForceFirstTimeUser(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Debug media cards (grid view + ?debug)</label>
              <Switch
                checked={debugStore.mediaCardsDebugMode}
                onChange={(checked) => debugStore.setMediaCardsDebugMode(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Overlay grid rank on cells</label>
              <Switch
                checked={debugStore.gridRankDebugMode}
                onChange={(checked) => debugStore.setGridRankDebugMode(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Mixpanel tracking</label>
              <Switch
                checked={trackingStore.trackingEnabled}
                onChange={(checked) => trackingStore.setTrackingEnabled(checked)}
                disabled={!inDebugMode}
              />
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Show Mixpanel event log</label>
              <Switch
                checked={trackingStore.eventLogEnabled}
                onChange={(checked) => trackingStore.setEventLogEnabled(checked)}
                disabled={!inDebugMode}
              />
            </div>
            {trackingStore.eventLogEnabled && <EventLogPanel />}
            {/* Open modal stack, bottom → top, duplicates included. Every consumer of ModalStore
                fails silently when this is wrong (drag-drop stops working, Esc stops walking
                back, ⌘F stops firing), so showing it turns an unbalanced registration into
                something visible instead of a mystery bug report. Should read "(none)" whenever
                no modal is on screen. */}
            <div className={css.debugField}>
              <label className={css.debugLabel}>Open modal stack</label>
              <span>{modalStore.debugModalStack.join(' → ') || '(none)'}</span>
            </div>
            <div className={css.debugField}>
              <label className={css.debugLabel}>Billing overrides (reset on refetch)</label>
            </div>
            <div className={css.debugField}>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  const displayCredits =
                    (billingStore.creditsLeft ?? 0) / CREDITS_API_TO_DISPLAY_SCALE;
                  const value = prompt(
                    'Set credits to (display credits):',
                    String(Math.round(displayCredits)),
                  );
                  if (value === null) return;
                  const num = parseFloat(value);
                  if (isNaN(num)) return;
                  runInAction(() => {
                    billingStore.debugCreditsOverride = num * CREDITS_API_TO_DISPLAY_SCALE;
                  });
                }}
                disabled={!inDebugMode}
              >
                Set credits
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  const storageHours = billingStore.storageLeft / SECONDS_IN_HOUR;
                  const value = prompt(
                    'Set storage left (hours):',
                    String(Math.round(storageHours * 100) / 100),
                  );
                  if (value === null) return;
                  const num = parseFloat(value);
                  if (isNaN(num)) return;
                  runInAction(() => {
                    billingStore.debugStorageLeftOverride = num * SECONDS_IN_HOUR;
                  });
                }}
                disabled={!inDebugMode}
              >
                Set storage
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  runInAction(() => {
                    billingStore.debugCreditsOverride = null;
                    billingStore.debugStorageLeftOverride = null;
                  });
                }}
                disabled={!inDebugMode}
              >
                Reset billing
              </ActionButton>
            </div>
            {/* Force the paywall regardless of billing, to see either wall from any
                account. Lives on the paywall store (not localStorage) so a reload
                clears it and the real verdict comes back. The Debug tab stays
                reachable behind the wall so this can be switched off again. */}
            <div className={css.debugField}>
              <label className={css.debugLabel}>
                Force paywall{' '}
                {paywallStore.debugForcedAccess ? `(${paywallStore.debugForcedAccess})` : '(off)'}
              </label>
            </div>
            <div className={css.debugField}>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  runInAction(() => {
                    paywallStore.debugForcedAccess = 'no-plan';
                  });
                }}
                disabled={!inDebugMode}
              >
                No plan
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  runInAction(() => {
                    paywallStore.debugForcedAccess = 'lapsed';
                  });
                }}
                disabled={!inDebugMode}
              >
                Lapsed subscription
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  runInAction(() => {
                    paywallStore.debugForcedAccess = null;
                  });
                }}
                disabled={!inDebugMode}
              >
                Off
              </ActionButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
});
